import React, { useState, useEffect } from 'react';
import { Plus, Save, Trash2, Edit } from 'lucide-react';
import api from '../services/api';

export default function PublicHolidaysConfig({ tenantId }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const defaultHoliday = {
    name: '',
    startDate: '',
    endDate: '',
    type: 'PUBLIC'
  };

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tenants/${tenantId}/admin/calendar/holidays`);
      setHolidays(res.data.data);
    } catch (err) {
      alert('Failed to load public holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [tenantId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingHoliday.name || !editingHoliday.startDate || !editingHoliday.endDate) {
      alert('Please fill out the holiday name and dates.');
      return;
    }
    try {
      await api.post(`/tenants/${tenantId}/admin/calendar/holidays`, editingHoliday);
      setEditingHoliday(null);
      fetchHolidays();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this public holiday?')) return;
    try {
      await api.delete(`/tenants/${tenantId}/admin/calendar/holidays/${id}`);
      fetchHolidays();
    } catch (err) {
      alert('Delete failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ margin: 0 }}>Public & Exceptional Holidays</h3>
        <button className="btn btn-outline" onClick={() => setEditingHoliday(defaultHoliday)}>
          <Plus size={16} /> New Holiday
        </button>
      </div>

      {editingHoliday && (
        <form onSubmit={handleSave} style={{ background: 'var(--bg-secondary)', padding: '24px', borderRadius: '8px', marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '16px' }}>{editingHoliday.id ? 'Edit Holiday' : 'Create New Holiday'}</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Holiday Name</label>
              <input type="text" className="form-input" value={editingHoliday.name} onChange={e => setEditingHoliday({...editingHoliday, name: e.target.value})} placeholder="e.g. National Day, Eid" />
            </div>

            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-input" value={editingHoliday.type} onChange={e => setEditingHoliday({...editingHoliday, type: e.target.value})}>
                <option value="PUBLIC">Public Holiday</option>
                <option value="EXCEPTIONAL">Exceptional Company Holiday</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input type="date" className="form-input" value={editingHoliday.startDate} onChange={e => setEditingHoliday({...editingHoliday, startDate: e.target.value})} />
            </div>
            
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input type="date" className="form-input" value={editingHoliday.endDate} onChange={e => setEditingHoliday({...editingHoliday, endDate: e.target.value})} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={() => setEditingHoliday(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary"><Save size={16} /> Save</button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-muted)' }}>
              <th style={{ padding: '12px' }}>Name</th>
              <th style={{ padding: '12px' }}>Type</th>
              <th style={{ padding: '12px' }}>Dates</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {holidays.map(h => (
              <tr key={h.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '12px', fontWeight: 500 }}>{h.name}</td>
                <td style={{ padding: '12px' }}>{h.type}</td>
                <td style={{ padding: '12px' }}>{h.startDate} to {h.endDate}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <button className="btn" style={{ padding: '4px', background: 'none' }} onClick={() => handleDelete(h.id)}>
                    <Trash2 size={16} color="var(--accent-rose)" />
                  </button>
                </td>
              </tr>
            ))}
            {holidays.length === 0 && (
              <tr><td colSpan="4" style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)' }}>No holidays found.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
